import { collection, doc, DocumentData, getDoc, getDocs, query, QueryConstraint, QueryDocumentSnapshot, setDoc, Timestamp, updateDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { fireDb, fireFunctions } from "../firebase-config";
import { DBUser, House, Owner, ReservationStatus, Room, RoomReservation, Settings, THouses, UserRole } from "../ostertalertypes";

export enum DB_ERROR {
  DOES_NOT_EXIST
}

export class DBError extends Error {
  constructor(message:string, public errorCode: number) {
    super(message);
  }
}

/**
 * Required for Autocompletion of users.
 * @returns
 */
export async function getAllUsers() {
  const users: Map<string, DBUser> = new Map();
  let usersSnapshot = await getDocs(collection(fireDb, "/users"));
  usersSnapshot.forEach(doc => {
    users.set(doc.id, databaseToUser(doc));
  });
  return users;
}

export async function getUserDetails(userid: string): Promise<DBUser | null> {
  if (!userid) {
    return null;
  }
  const colRef = collection(fireDb, "/users");
  const q = query(colRef, where("userid", "==", userid));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.size === 1) {
    const user = databaseToUser(querySnapshot.docs[0]);
    return user;
  }
  return null;
};

export async function getIncomingRequests(adminHouses?: string[]): Promise<number> {
  let constraints: QueryConstraint[] = [
    where("status", "in", [ReservationStatus.BOOKING_REQUESTED, ReservationStatus.CANCEL_REQUESTED])
  ];

  // If admin houses are specified, filter by those houses
  if (adminHouses && adminHouses.length > 0) {
    constraints.push(where("houseName", "in", adminHouses));
  }

  const q = query(
    collection(fireDb, "/reservations"),
    ...constraints
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.size;
};

export async function fetchAllocationBookings(withUsers:boolean, ...constraints: QueryConstraint[]): Promise<RoomReservation[]> {
  const newAppReservations: RoomReservation[] = [];
  const reservationsRef = collection(fireDb, "/reservations");
  const q = query(reservationsRef, ...constraints);
  const querySnapshot = await getDocs(q);
  for (const doc of querySnapshot.docs) {
    const appRes: RoomReservation = databaseToReservation(doc);
    if (withUsers && appRes.userid) {
      if (withUsers) {
        const details = await getUserDetails(appRes.userid);
        if (details) {
          appRes.user = details;
        } else {
          const userRef = collection(fireDb, `/reservations/${doc.id}/user`);
          const querySnapshot = await getDocs(userRef);
          if (querySnapshot.size === 1) {
            appRes.user = databaseToUser(querySnapshot.docs[0]);
          } else {
            console.error(`No user with id ${appRes.userid} found`)
            appRes.user = {firstname:"Gelöschter Benutzer", lastname: "", userid:"", bookingCount: 0}
          }
        }
      }
    }
    newAppReservations.push(appRes);
  }
  return newAppReservations;
}

function databaseToReservation(doc:QueryDocumentSnapshot<DocumentData>): RoomReservation {
  let data = doc.data();
  return {
    id: doc.id,
    userid: data.userid,
    fullHouse: data.fullHouse,
    adminComment: data.adminComment,
    userComment: data.userComment,
    roomName: data.roomName,
    from: new Timestamp(data.from.seconds, data.from.nanoseconds),
    to: new Timestamp(data.to.seconds, data.to.nanoseconds),
    status: data.status,
    houseName: data.houseName,
    lastUpdate: data.lastUpdate,
    createdDate: data.createdDate,
    booker: data.booker,
    guestCount: data.guestCount
  };
}

function databaseToUser(doc:QueryDocumentSnapshot<DocumentData>): DBUser {
  let data = doc.data();
  return {
    userid: doc.id,
    firstname: data.firstname,
    lastname: data.lastname,
    role: data.role === undefined ? UserRole.NORMAL_ACCOUNT: data.role,
    bookingCount: data.bookingCount === undefined ? 0 : data.bookingCount
  };
}

export async function fetchHouses(): Promise<THouses> {
  let houses: Map<string, House> = new Map();
  let rooms: Map<string, Room> = new Map();
  const housesRef = collection(fireDb, "/houses");
  const houseSnapshot = await getDocs(housesRef);
  for (let doc of houseSnapshot.docs) {
    let data = doc.data();
    const roomsRef = collection(fireDb, "/houses/" + doc.id + "/rooms");
    const roomSnapshot = await getDocs(roomsRef);
    for (let docRoom of roomSnapshot.docs) {
      let data = docRoom.data();
      rooms.set(data.roomName, {
        roomName: data.roomName,
        description: data.description,
        capacity: data.capacity
      })
    }
    const sortedRooms = new Map([...rooms.entries()].sort());
    houses.set(data.houseName.toLowerCase(), {
      houseName: data.houseName,
      description: data.description,
      fullHouseOption: data.fullHouseOption,
      capacity: data.capacity,
      rooms: sortedRooms
    })
    rooms.clear();
  };
  return houses;
}

// export async function fetchAllocationBookingsWithUsers(...constraints: QueryConstraint[]): Promise<RoomReservation[]> {
//   let newAppReservations = await fetchAllocationBookings(...constraints);
//   let users: Map<string, DBUser> = await getAllUsers();
//   newAppReservations.forEach(res => {
//     res.user = users.get(res.userid as string)
//   })
//   return newAppReservations;
// }


/**
 * Creates reservations via cloud function. The server checks in a transaction that no
 * overlapping reservation exists. Throws a FirebaseError (code "functions/already-exists") on conflict.
 * @param reservations reservations to create
 * @param userid only for admins: create the reservations for another user
 * @param anonymousUser only for admins: create a new anonymous guest account together with the reservations
 */
export async function createReservations(
  reservations: RoomReservation[], userid?: string, anonymousUser?: { firstName: string, lastName: string }
): Promise<{ ids: string[], userid: string }> {
  const call = httpsCallable(fireFunctions, "createReservations");
  const result = await call({
    userid,
    anonymousUser,
    reservations: reservations.filter(r => r.from && r.to).map(r => ({
      from: r.from!.toMillis(),
      to: r.to!.toMillis(),
      houseName: r.houseName,
      roomName: r.roomName,
      fullHouse: r.fullHouse,
      guestCount: r.guestCount,
      userComment: r.userComment ?? "",
      adminComment: r.adminComment ?? "",
    })),
  });
  return result.data as { ids: string[], userid: string };
}

/**
 * Admin only: changes room, dates and guests of a reservation. Conflicts are checked on the server.
 * @param accept also accept the reservation, if it is still a booking request.
 */
export async function updateReservation(reservation: RoomReservation, accept: boolean = false) {
  const call = httpsCallable(fireFunctions, "updateReservation");
  await call({
    id: reservation.id,
    from: reservation.from!.toMillis(),
    to: reservation.to!.toMillis(),
    roomName: reservation.roomName,
    fullHouse: reservation.fullHouse,
    guestCount: reservation.guestCount,
    adminComment: reservation.adminComment,
    userid: reservation.userid,
    status: accept ? ReservationStatus.ACCEPTED : undefined,
  });
}

/**
 * Changes status and comments of a reservation via cloud function.
 * Admins may change everything, normal users may only request the cancellation of their own reservation.
 */
export async function updateReservationStatus(reservation: RoomReservation, attributes: ("status" | "adminComment" | "userComment")[]) {
  const call = httpsCallable(fireFunctions, "updateReservationStatus");
  const changes: { [k: string]: unknown } = { id: reservation.id };
  attributes.forEach(att => {
    changes[att] = reservation[att];
  });
  await call(changes);
}

export async function fetchOwner(): Promise<Owner> {
  const docRef = doc(fireDb, "owner", "contact");
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    const owner: Owner = { name: data.name, email: data.email, impressum: data.impressum, phone: data.phone }
    return owner;
  }
  throw new DBError("Owner database not setup correctly", DB_ERROR.DOES_NOT_EXIST);
};

export async function fetchSettings(): Promise<Settings> {
  const docRef = doc(fireDb, "settings", "reservations");
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    const settings: Settings = { reservation: {
       emailOnReservationUpdate: data.emailOnReservationUpdate,
       maxAllowedBookingDate: data.maxAllowedBookingDate,
       allocationCheckerPublic: data.allocationCheckerPublic,
       bookingRestrictionDays: data.bookingRestrictionDays
      }
    }
    return settings;
  }
  throw new DBError("Settings database not setup correctly", DB_ERROR.DOES_NOT_EXIST);
};


export async function updateSettings(settings: Settings): Promise<void> {
  const collDest = "settings/reservations";
  const reservationRef = doc(fireDb, collDest);
  await updateDoc(reservationRef, {...settings.reservation} );
};

export async function createSettings(settings: Settings): Promise<void> {
  const collDest = "settings/reservations";
  const reservationRef = doc(fireDb, collDest);
  await setDoc(reservationRef, {...settings.reservation} );
};

/**
 * Save deleted user in subcollection of reservation so that the name can be still displayed.
 * @param user
 */
export async function rescueDeletedUser(user: DBUser) {
  const reservationsRef = collection(fireDb, "/reservations");
  const q = query(reservationsRef, where("userid", "==", user.userid));
  const querySnapshot = await getDocs(q);
  for (const res of querySnapshot.docs) {
    const userRef = doc(fireDb, `reservations/${res.id}/user/${user.userid}`);
    await setDoc(userRef, {
      userid: user.userid,
      firstname: "Gelöschter Benutzer " + user.firstname,
      lastname: user.lastname
    });
  }
}
